import React, { useRef, useEffect } from "react";
import classnames from "classnames";

import { GenericObject } from "../types";
import { Row, RowSelectionState, RowPinningState } from "@tanstack/react-table";

import { buildAriaProps, buildCss, buildDataProps, buildHtmlProps } from "../utilities/props";
import { globalProps, GlobalProps } from "../utilities/globalProps";

import Table from "../pb_table/_table";
import { AdvancedTableProvider } from "./Context/AdvancedTableContext";
import { getVirtualizedContainerStyles } from "./Utilities/TableContainerStyles";

import { TableHeader } from "./SubKits/TableHeader";
import { TableBody } from "./SubKits/TableBody";
import TablePagination from "./Components/TablePagination";
import TableActionBar from "./Components/TableActionBar";

import { useTableState } from "./Hooks/useTableState";
import { useTableActions } from "./Hooks/useTableActions";
import { updateStickyLayoutHeights, scheduleStickyActionBarHeightUpdate } from "./Utilities/StickyLayoutHelper";

type AdvancedTableProps = {
  aria?: { [key: string]: string }
  actions?: React.ReactNode[] | React.ReactNode
  cascadeCollapse?: boolean
  children?: React.ReactNode | React.ReactNode[]
  className?: string
  columnDefinitions: GenericObject[]
  columnGroupBorderColor?: "text_lt_default" | "text_lt_light" | "text_lt_lighter" | "text_dk_default" | "text_dk_light" | "text_dk_lighter"
  columnVisibilityControl?: GenericObject
  customSort?: boolean
  data?: { [key: string]: string }
  enableToggleExpansion?: "all" | "header" | "none"
  enableSortingRemoval?: boolean
  expandedControl?: GenericObject
  expandByDepth?: GenericObject[]
  onExpandByDepthClick?: (arg: number, arg1: any) => void
  fullWidthCell?: boolean
  htmlOptions?: {[key: string]: string | number | boolean | (() => void)},
  id?: string
  initialLoadingRowsCount?: number
  inlineRowLoading?: boolean
  loading?: boolean | string
  maxHeight?: "auto" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl" | "xxxl"
  onRowToggleClick?: (arg: Row<GenericObject>) => void
  onToggleExpansionClick?: (arg: Row<GenericObject>) => void
  pagination?: boolean,
  paginationProps?: GenericObject,
  pinnedRows?: {
    value?: RowPinningState;
    onChange?: (value: RowPinningState) => void;
  };
  responsive?: "scroll" | "none",
  rowStyling?: GenericObject[],
  scrollBarNone?: boolean,
  selectableRows?: boolean,
  showActionsBar?: boolean,
  persistToggleExpansionButton?: boolean,
  sortControl?: GenericObject
  sortParentOnly?: boolean
  stickyLeftColumn?: string[]
  tableData: GenericObject[]
  tableOptions?: GenericObject
  tableProps?: GenericObject
  toggleExpansionIcon?: string | string[]
  onRowSelectionChange?: (arg: RowSelectionState) => void
  onCustomSortClick?: (arg: GenericObject[]) => void
  virtualizedRows?: boolean
} & GlobalProps;

/**
 * Subcomponent props (AdvancedTable.Header / AdvancedTable.Body). Must be a plain `type NameProps = { ... }` block—
 * `yarn generate-ai-metadata` only parses that shape in this file (not Omit<> / intersection).
 * Not used on the root component; playground routes these via propTargets.
 */
type _AdvancedTableSubkitSchemaProps = {
  enableSorting?: boolean
  sortIcon?: string | string[]
  collapsibleTrail?: boolean
  subRowHeaders?: string[]
}

const AdvancedTable = (props: AdvancedTableProps) => {
  const {
    aria = {},
    actions,
    cascadeCollapse = false,
    children,
    className,
    columnDefinitions,
    columnGroupBorderColor,
    columnVisibilityControl,
    customSort,
    dark = false,
    data = {},
    enableToggleExpansion = "header",
    enableSortingRemoval = false,
    expandedControl,
    expandByDepth,
    onExpandByDepthClick,
    fullWidthCell = false,
    htmlOptions = {},
    id,
    initialLoadingRowsCount = 10,
    inlineRowLoading = false,
    loading,
    maxHeight,
    onRowToggleClick,
    onToggleExpansionClick,
    onCustomSortClick,
    pagination = false,
    paginationProps,
    pinnedRows,
    responsive = "scroll",
    rowStyling,
    scrollBarNone= false,
    showActionsBar = true,
    selectableRows,
    persistToggleExpansionButton = false,
    sortControl,
    sortParentOnly = false,
    stickyLeftColumn,
    tableData,
    tableOptions,
    tableProps,
    toggleExpansionIcon = "arrows-from-line",
    onRowSelectionChange,
    virtualizedRows = false,
  } = props;

  const noTableCardContainer = tableProps?.container === false;

  // Component refs
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const {
    table,
    expanded,
    setExpanded,
    hasAnySubRows,
    selectedRowsLength,
    fetchNextPage,
    updateLoadingStateRowCount,
    fullData,
    totalFetched,
    isFetching,
    localPagination,
    setLocalPagination
  } = useTableState({
    tableData,
    columnDefinitions,
    enableSortingRemoval,
    expandedControl,
    sortControl,
    onRowToggleClick,
    selectableRows,
    initialLoadingRowsCount,
    loading,
    pagination,
    paginationProps,
    virtualizedRows,
    tableOptions,
    onRowSelectionChange,
    columnVisibilityControl,
    pinnedRows,
    rowStyling,
    inlineRowLoading,
    sortParentOnly,
    fullWidthCell,
  });

  // Initialize table actions
  const {
    handleExpandOrCollapse,
    onPageChange,
    fetchMoreOnBottomReached
  } = useTableActions({
    table,
    expanded,
    setExpanded,
    onToggleExpansionClick,
    onRowSelectionChange,
    inlineRowLoading,
    localPagination,
    setLocalPagination,
    cascadeCollapse
  });

  // Set table row count for loading state
  useEffect(() => {
    if (!loading) {
      updateLoadingStateRowCount();
    }
  }, [loading, updateLoadingStateRowCount]);

  // Check for infinite scroll
  useEffect(() => {
    fetchMoreOnBottomReached(
      tableWrapperRef.current,
      fetchNextPage,
      isFetching,
      totalFetched,
      fullData.length
    );
  }, [fetchMoreOnBottomReached, fetchNextPage, isFetching, totalFetched, fullData.length]);

  // Build CSS classes and props
  const ariaProps = buildAriaProps(aria);
  const dataProps = buildDataProps(data);
  const htmlProps = buildHtmlProps(htmlOptions);

  // Visibility flag for action bar
  const isActionBarVisible = (selectableRows && showActionsBar && selectedRowsLength > 0) || columnVisibilityControl;
  const isStickyHeader = Boolean(tableProps?.sticky);

  useEffect(() => {
    if (!isStickyHeader || !tableWrapperRef.current) return;

    const wrapper = tableWrapperRef.current;
    const updateHeights = () => updateStickyLayoutHeights(wrapper);

    updateHeights();
    scheduleStickyActionBarHeightUpdate(wrapper);

    const resizeObserver = new ResizeObserver(updateHeights);
    resizeObserver.observe(wrapper);

    const actionBar = wrapper.querySelector(".row-selection-actions-card");
    if (actionBar) {
      resizeObserver.observe(actionBar);
    }

    const table = wrapper.querySelector("table.pb_table");
    const thead = table?.querySelector("thead");
    if (thead) {
      resizeObserver.observe(thead);
    }

    return () => resizeObserver.disconnect();
  }, [isStickyHeader, isActionBarVisible, columnDefinitions, tableData]);

  const classes = classnames(
    buildCss("pb_advanced_table"),
    `advanced-table-responsive-${responsive}`,
    maxHeight ? `advanced-table-max-height-${maxHeight}` : '',
    {
      // Add the hidden-action-bar class when action bar functionality exists but is not visible
      'hidden-action-bar': (selectableRows || columnVisibilityControl) && !isActionBarVisible,
    },
    {'advanced-table-sticky-left-columns': stickyLeftColumn && stickyLeftColumn.length > 0},
    { 'advanced-table-sticky-header': isStickyHeader },
    { 'advanced-table-no-table-container': noTableCardContainer },
    columnGroupBorderColor ? `column-group-border-${columnGroupBorderColor}` : '',
    scrollBarNone ? 'advanced-table-hide-scrollbar' : '',
    globalProps(props),
    className
  );

  // Table wrapper styling with virtualization support
  const tableWrapperStyle = virtualizedRows
    ? getVirtualizedContainerStyles(maxHeight)
    : {};

  // The actual Main <Table /> element
  const tableElement = (
    <Table
        className={`${loading ? "content-loading" : ""}`}
        dark={dark}
        dataTable
        numberSpacing="tabular"
        responsive="none"
        {...tableProps}
    >
      {children ? (
        children
      ) : (
        <>
          <TableHeader />
          <TableBody
              isFetching={isFetching}
          />
        </>
      )}
    </Table>
  )

  return (
    <>
      {/* Top Pagination */}
      {pagination && (
        <TablePagination
            onChange={onPageChange}
            position="top"
            range={paginationProps?.range}
            sectionSeparator={paginationProps?.sectionSeparator}
            table={table}
        />
      )}

      <div
          {...ariaProps}
          {...dataProps}
          {...htmlProps}
          className={classes}
          id={id}
          onScroll={virtualizedRows ? e => fetchMoreOnBottomReached(
            e.currentTarget,
            fetchNextPage,
            isFetching,
            totalFetched,
            fullData.length
          ) : undefined}
          ref={tableWrapperRef}
          style={tableWrapperStyle as React.CSSProperties}
      >
        <AdvancedTableProvider
            cascadeCollapse={cascadeCollapse}
            columnDefinitions={columnDefinitions}
            columnGroupBorderColor={columnGroupBorderColor}
            columnVisibilityControl={columnVisibilityControl}
            customSort={customSort}
            enableSortingRemoval={enableSortingRemoval}
            enableToggleExpansion={enableToggleExpansion}
            enableVirtualization={virtualizedRows}
            expandByDepth={expandByDepth}
            expanded={expanded}
            expandedControl={expandedControl}
            handleExpandOrCollapse={handleExpandOrCollapse}
            hasAnySubRows={hasAnySubRows}
            inlineRowLoading={inlineRowLoading}
            isActionBarVisible={isActionBarVisible}
            loading={loading}
            onCustomSortClick={onCustomSortClick}
            onExpandByDepthClick={onExpandByDepthClick}
            persistToggleExpansionButton={persistToggleExpansionButton}
            pinnedRows={pinnedRows}
            responsive={responsive}
            rowStyling={rowStyling}
            selectableRows={selectableRows}
            setExpanded={setExpanded}
            showActionsBar={showActionsBar}
            sortControl={sortControl}
            stickyLeftColumn={stickyLeftColumn}
            subRowHeaders={tableOptions?.subRowHeaders}
            table={table}
            tableContainerRef={tableWrapperRef}
            toggleExpansionIcon={toggleExpansionIcon}
            totalAvailableCount={fullData.length}
            virtualizedRows={virtualizedRows}
        >
          <React.Fragment>
            {/* Selection Action Bar */}
            <TableActionBar
                actions={actions}
                isVisible={isActionBarVisible}
                selectedCount={selectedRowsLength}
                type={columnVisibilityControl ? "column-visibility" : "row-selection"}
            />

            {/* Virtualized wrapper div only if virtualizedRows is true */}
            {virtualizedRows ? (
              <div style={{ overflow: 'auto', width: '100%' }}>
                {tableElement}
              </div>
            ) : (
              tableElement
            )}
          </React.Fragment>
        </AdvancedTableProvider>

      </div>
      {/* Bottom Pagination */}
      {pagination && (
        <TablePagination
            onChange={onPageChange}
            position="bottom"
            range={paginationProps?.range}
            sectionSeparator={paginationProps?.sectionSeparator}
            table={table}
        />
      )}
    </>
  );
};

// Re-export sub-components
AdvancedTable.Header = TableHeader;
AdvancedTable.Body = TableBody;

export default AdvancedTable;
